import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  fullName: "",
  relationship: "",
  email: "",
  phone: "",
  location: "",
  serviceInterest: "",
  message: "",
};

const inputClass =
  "rounded-xl border-navy/15 bg-white text-navy placeholder:text-charcoal/40 focus-visible:ring-mauve";

export const EnquiryForm = ({ compact = false, submitTestId, idPrefix = "enquiry", onSuccess }) => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        full_name: form.fullName,
        relationship: form.relationship || null,
        email: form.email,
        phone: form.phone || null,
        location: form.location || null,
        service_interest: form.serviceInterest || null,
        message: form.message || null,
      });
      toast.success("Thank you — your enquiry is on its way.", {
        description: "Our care coordinator will reach out within 24 hours.",
      });
      setForm(EMPTY);
      onSuccess?.();
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again, or call us on 0800 920 4821.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-5 sm:grid-cols-2" data-testid={`${idPrefix}-form`}>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`} className="text-navy">Your Name *</Label>
        <Input
          id={`${idPrefix}-name`}
          data-testid={`${idPrefix}-name-input`}
          required
          value={form.fullName}
          onChange={(e) => set("fullName", e.target.value)}
          placeholder="e.g. Sarah Jenkins"
          className={inputClass}
        />
      </div>

      {!compact && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-relationship`} className="text-navy">Relationship to Loved One</Label>
          <Input
            id={`${idPrefix}-relationship`}
            data-testid={`${idPrefix}-relationship-input`}
            value={form.relationship}
            onChange={(e) => set("relationship", e.target.value)}
            placeholder="e.g. Daughter / Son / Self"
            className={inputClass}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-email`} className="text-navy">Email Address *</Label>
        <Input
          id={`${idPrefix}-email`}
          data-testid={`${idPrefix}-email-input`}
          type="email"
          required
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="sarah@example.co.uk"
          className={inputClass}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-phone`} className="text-navy">Phone Number</Label>
        <Input
          id={`${idPrefix}-phone`}
          data-testid={`${idPrefix}-phone-input`}
          type="tel"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          placeholder="07700 900000"
          className={inputClass}
        />
      </div>

      {!compact && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-location`} className="text-navy">Postcode / Town in UK</Label>
          <Input
            id={`${idPrefix}-location`}
            data-testid={`${idPrefix}-location-input`}
            value={form.location}
            onChange={(e) => set("location", e.target.value)}
            placeholder="e.g. Richmond, TW9 or Edinburgh, EH1"
            className={inputClass}
          />
        </div>
      )}

      <div className={`space-y-2 ${compact ? "sm:col-span-2" : ""}`}>
        <Label htmlFor={`${idPrefix}-service`} className="text-navy">Service of Interest</Label>
        <Select value={form.serviceInterest} onValueChange={(v) => set("serviceInterest", v)}>
          <SelectTrigger
            id={`${idPrefix}-service`}
            data-testid={`${idPrefix}-service-select`}
            className={inputClass}
          >
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {SERVICE_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt} data-testid={`${idPrefix}-service-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${idPrefix}-message`} className="text-navy">
          Tell us about their interests, hobbies & routine
        </Label>
        <Textarea
          id={`${idPrefix}-message`}
          data-testid={`${idPrefix}-message-input`}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder="Mum loves classical radio, gardening, and reminiscing about Scotland..."
          rows={compact ? 3 : 5}
          className={inputClass}
        />
      </div>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid={submitTestId}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-cream shadow-elevated transition-all duration-300 hover:-translate-y-0.5 hover:bg-mauve-dark disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="h-4 w-4" aria-hidden="true" />
          )}
          {submitting ? "Sending..." : "Send My Enquiry"}
        </button>
        <p className="mt-3 text-xs text-charcoal/50">
          We respond within 24 hours, Monday to Friday. Your details are never shared.
        </p>
      </div>
    </form>
  );
};
